import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import InstagramIcon from '@mui/icons-material/Instagram';
import { getFeaturedCategories } from '../../assets/categoryImages';
import { useSelectedCountry } from '../../hooks/useSelectedCountry';
import { InstallAppLink } from '../ui/InstallAppLink';
import { CommunityJoinButton } from '../ui/CommunityCta';

export function Footer() {
  const { t } = useTranslation();
  const { countryCode } = useSelectedCountry();
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        // Theme-aware: warm cream paper in light mode, near-black in dark mode.
        bgcolor: 'background.paper',
        color: 'text.secondary',
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        {/* Community CTA — full-width WhatsApp pill at the top of the
            footer. Self-hides when VITE_COMMUNITY_WHATSAPP_URL is unset
            so we don't dangle a dead link in dev / staging. */}
        <Box sx={{ mb: { xs: 4, md: 5 }, textAlign: 'center' }}>
          <CommunityJoinButton sx={{ width: { xs: '100%', sm: 'auto' } }} />
        </Box>

        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* BRAND — black wordmark (icon + "Afro Espace") on transparent
              background. The image carries the brand mark so no separate
              text rendering is needed. {@code alt} keeps the brand name
              accessible to screen readers and SEO crawlers. */}
          <Grid item xs={12} md={3}>
            <Box
              component={RouterLink}
              to="/"
              sx={{ display: 'inline-block', lineHeight: 0 }}
              aria-label={t('app.name')}
            >
              <Box
                component="img"
                src="/logo-noir.png"
                alt={t('app.name')}
                loading="lazy"
                sx={{
                  display: 'block',
                  height: { xs: 48, md: 56 },
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ mt: 2.5 }}>
              <IconButton
                component="a"
                href="https://www.linkedin.com/in/winor-group-692b20406"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn — Winor Group"
                size="small"
                sx={{
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.facebook.com/profile.php?id=61589170833002"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook — Winor Group"
                size="small"
                sx={{
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <FacebookRoundedIcon fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.instagram.com/winorgroup0/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram — Winor Group"
                size="small"
                sx={{
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <InstagramIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>

          {/* ABOUT */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterHeading>{t('footer.aboutTitle')}</FooterHeading>
            <Stack spacing={1.25} sx={{ mt: 2 }}>
              <FooterLink to="/pro">{t('footer.imAPro')}</FooterLink>
              <FooterLink to="/pro">{t('nav.becomeProvider')}</FooterLink>
              <FooterLink to="/terms">{t('footer.terms')}</FooterLink>
              <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
              <FooterLink to="/legal">{t('footer.legalNotice')}</FooterLink>
              <FooterLink to="/contact">{t('footer.contact')}</FooterLink>
              {/* Self-hides once the visitor already runs AfroEspace as
                  an installed PWA (display-mode: standalone). */}
              <InstallAppLink />
            </Stack>
          </Grid>

          {/* FIND A SERVICE */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterHeading>{t('footer.findServiceTitle')}</FooterHeading>
            <Stack spacing={1.25} sx={{ mt: 2 }}>
              {getFeaturedCategories(countryCode).map((cat) => (
                <FooterLink key={cat.slug} to={`/category/${cat.slug}`}>
                  {t(cat.i18nKey)}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* POPULAR CITIES — hidden for now; the seeded city list isn't
              focused on the FR/IT/DE/BE launch markets so the section
              displayed off-brand entries (Austrian cities etc.). Will come
              back when we curate a per-country top-list. */}
        </Grid>

        <Divider sx={{ my: { xs: 4, md: 5 } }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            © {year} {t('app.name')} — {t('footer.allRightsReservedBy')}{' '}
            <Link
              href="https://winorgroup.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { color: 'primary.main', textDecoration: 'underline' },
              }}
            >
              Winorgroup
            </Link>
            .
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function FooterHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        color: 'text.primary',
        fontWeight: 700,
        fontSize: '0.9375rem',
        letterSpacing: '0.01em',
      }}
    >
      {children}
    </Typography>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      component={RouterLink}
      to={to}
      underline="none"
      sx={{
        color: 'text.secondary',
        fontSize: '0.9375rem',
        lineHeight: 1.4,
        transition: 'color 150ms ease',
        '&:hover': { color: 'primary.main' },
      }}
    >
      {children}
    </Link>
  );
}
